// src/utils/reservationUtils.ts
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE = 'America/New_York';

export const isInBreakHours = (siteContent: any): boolean => {
  const hours = (siteContent?.data as any)?.reservation?.info?.hours;
  if (!hours) {
    return false;
  }
  
  const nowET = dayjs().tz(TIMEZONE);
  const dayIndex = nowET.day();
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayNames[dayIndex];
  const dayHours = hours[dayName];
  
  if (!dayHours || dayHours.is_closed) return false;
  
  if (!dayHours.firstHalf?.end || !dayHours.secondHalf?.start) {
    return false;
  }
  
  const breakStart = dayjs.tz(`${nowET.format('YYYY-MM-DD')} ${dayHours.firstHalf.end}`, TIMEZONE);
  const breakEnd = dayjs.tz(`${nowET.format('YYYY-MM-DD')} ${dayHours.secondHalf.start}`, TIMEZONE);
  
  return nowET.isAfter(breakStart) && nowET.isBefore(breakEnd);
};

export const checkIfRestaurantClosed = (dateToCheck: string, siteContent: any): boolean => {
  const hours = (siteContent?.data as any)?.reservation?.info?.hours;
  if (!hours) {
    return false;
  }
  
  const [year, month, day] = dateToCheck.split('-').map(Number);
  const dayIndex = new Date(year, month - 1, day).getDay();
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayNames[dayIndex];
  const dayHours = hours[dayName];
  
  if (!dayHours) {
    return true;
  }

  // Respect explicit closure flags first (e.g., holidays, special closures)
  if (dayHours.is_closed === true) {
    return true;
  }

  // Check if valid shifts are configured
  const hasFirstHalf = Boolean(
    dayHours.firstHalf?.start?.trim() && dayHours.firstHalf?.end?.trim()
  );
  const hasSecondHalf = Boolean(
    dayHours.secondHalf?.start?.trim() && dayHours.secondHalf?.end?.trim()
  );

  // If valid operating shifts exist, treat as open
  if (hasFirstHalf || hasSecondHalf) {
    return false;
  }

  // Otherwise default to closed
  return true;
};

export const formatStartTimesToEST = (data: any, selectedDate: string): any[] => {
  if (!data || !Array.isArray(data)) return [];

  const nowET = dayjs().tz(TIMEZONE);
  const selectedDateET = dayjs.tz(selectedDate, 'YYYY-MM-DD', TIMEZONE);
  const isToday = selectedDateET.isSame(nowET, 'day');
  
  const filtered = data.filter((item: any) => {
    if (!isToday) return true;
    const slotTimeET = dayjs.tz(item.start_time, TIMEZONE);
    return slotTimeET.isAfter(nowET);
  });

  return filtered.map((item: any) => {
    const startTimeET = dayjs.tz(item.start_time, TIMEZONE);
    return {
      start_time: startTimeET.format('h:mm A'),
      isAvailable: item.isAvailable ?? true
    };
  });
};

export const getStartAndEndTime = (dateStr: any, timeStr: any) => {
  const defaultTime = timeStr || '7:00 PM';
  const [time, meridian] = defaultTime.split(' ');
  let [hours, minutes] = time.split(':').map(Number);

  if (meridian?.toLowerCase() === 'pm' && hours !== 12) hours += 12;
  else if (meridian?.toLowerCase() === 'am' && hours === 12) hours = 0;

  const [year, month, day] = dateStr.split('-').map(Number);
  const start = new Date(year, month - 1, day, hours, minutes);
  const end = new Date(start.getTime() + 60 * 60 * 1000);

  const format = (d: any) => {
    const pad = (n: any) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  return { startTime: format(start), endTime: format(end) };
};